import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Cookie, X, Settings, ExternalLink } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'mando_cookie_consent';
const COOKIES_POLICY_URL = 'https://www.leapifyes.com/politica-de-cookies';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Delay showing banner for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const handleAcceptEssential = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up">
      <div className="max-w-4xl mx-auto bg-slate-900/95 backdrop-blur-xl border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Main Banner */}
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Cookie className="w-6 h-6 text-orange-500" />
            </div>
            
            <div className="flex-1">
              <h3 className="text-white font-semibold text-lg mb-2">
                Respetamos tu privacidad
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Utilizamos cookies para mejorar tu experiencia en nuestra web. Puedes aceptar todas, 
                rechazarlas o gestionar tus preferencias. Consulta nuestra{' '}
                <a 
                  href={COOKIES_POLICY_URL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-orange-400 hover:text-orange-300 inline-flex items-center gap-1"
                >
                  Política de Cookies
                  <ExternalLink className="w-3 h-3" />
                </a>
              </p>
              
              {/* Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleAcceptAll}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-6"
                >
                  Aceptar todas
                </Button>
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
                >
                  Rechazar
                </Button>
                <Button
                  onClick={() => setShowDetails(!showDetails)}
                  variant="ghost"
                  className="text-slate-400 hover:text-white"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Gestionar
                </Button>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={handleAcceptEssential}
              className="text-slate-500 hover:text-white transition-colors p-1"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Details Section */}
          {showDetails && (
            <div className="mt-6 pt-6 border-t border-slate-700">
              <div className="grid sm:grid-cols-3 gap-4">
                {/* Essential */}
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium text-sm">Esenciales</span>
                    <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">Siempre activas</span>
                  </div>
                  <p className="text-slate-400 text-xs">
                    Necesarias para el funcionamiento básico del sitio.
                  </p>
                </div>
                
                {/* Analytics */}
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium text-sm">Analíticas</span>
                    <span className="text-xs bg-slate-600 text-slate-300 px-2 py-1 rounded">Opcionales</span>
                  </div>
                  <p className="text-slate-400 text-xs">
                    Nos ayudan a entender cómo usas la web.
                  </p>
                </div>
                
                {/* Marketing */}
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium text-sm">Marketing</span>
                    <span className="text-xs bg-slate-600 text-slate-300 px-2 py-1 rounded">Opcionales</span>
                  </div>
                  <p className="text-slate-400 text-xs">
                    Para mostrarte contenido relevante.
                  </p>
                </div>
              </div>
              
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={handleAcceptEssential}
                  className="bg-slate-700 hover:bg-slate-600 text-white text-sm"
                >
                  Guardar preferencias (solo esenciales)
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
